import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Input, Popover } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { CheckboxOptionType, CheckboxValueType } from 'antd/lib/checkbox/Group';

import { AdditionalOption, TariffsResponse, Trial } from 'entities/tariff/api/contracts';
import { PricePerUnit } from 'entities/tariff/ui/PricePerUnits/PricePerUnits';
import { UserUnitItem } from 'entities/user/api/contracts';
import { usePrecalculation } from 'features/tariffs/modal/usePrecalculation';
import { SubscriptionOrder } from 'features/tariffs/subscribe/ui/SubscribeModals/SubscribeModalContainer/SubscribeModalContainer';
import { SubscribePrice } from 'features/tariffs/subscribe/ui/SubscribePrice/SubscribePrice';
import { isValidEmailAddress } from 'shared/lib/isValidEmailAddress';
import { InputErrorMessage } from 'shared/ui/InputErrorMessage';
import { ModalDropdown } from 'shared/ui/ModalDropdown/ModalDropdown';
import {
	AdditionalPriceStyled,
	BlockContainerStyled,
	ChooseUnitsContainerStyled,
	ChooseUnitsSelectedContainerStyled,
	ChooseUnitsSelectionHeadingStyled,
	ChooseUnitsTextStyled,
	FixedPriceContainerStyled,
	GridCheckboxGroupStyled,
	OldUsersContainerStyled,
	OrderInfoContainerStyled,
	OrderInfoItemStyled,
	OrderInfoItemTextStyled,
	PriceSummaryContainerStyled,
	PriceSummaryHeadingStyled,
	TermsOfServiceContainerStyled,
} from './SubscribeConfigure.styles';

import './SubscribeConfigure.css';

export interface SubscribeConfigureProps {
	tariff: TariffsResponse;
	units: UserUnitItem[];
	additionalFeatures: AdditionalOption[];
	toggleRulesAccepted: (n: boolean) => void;
	order: SubscriptionOrder;
	trial: Trial | null;
	updateOrder: (newOrder: SubscriptionOrder) => void;
}

export const SubscribeConfigure: FC<SubscribeConfigureProps> = ({
	tariff,
	units,
	additionalFeatures,
	order,
	updateOrder,
	toggleRulesAccepted,
	trial,
}) => {
	const CheckboxGroup = Checkbox.Group;

	const plainOptions: CheckboxOptionType[] = units.map(n => ({
		label: n.name,
		value: n.id,
	}));

	const defaultCheckedList = plainOptions.map(n => n.value);
	const defaultAdditionalFeaturesCheckedList: CheckboxValueType[] = [];

	const [checkedList, setCheckedList] = useState<CheckboxValueType[]>(defaultCheckedList);
	const [checkedFeaturesList, setCheckedFeaturesList] = useState<CheckboxValueType[]>(
		defaultAdditionalFeaturesCheckedList,
	);
	const [checkAll, setCheckAll] = useState(true);

	const hasSpecialPrice = tariff.tariffOverrides.filter(n => n.type === 1).length !== 0;

	const { precalculate, precalculatedPrice, isPrecalculationLoading } = usePrecalculation(order, tariff);

	useEffect(() => {
		void precalculate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [order.relatedEntityIds, order.additionalFeatures, tariff.isTrialAvailable, tariff.trial]);

	const additionalFeaturesOptions: CheckboxOptionType[] = additionalFeatures.map(n => ({
		label: `${n.name} (+${n.price ? n.price : ``} ₽ за заведение)`,
		value: n.id,
	}));

	const hasFixedPrice = tariff.tariffOverrides.find(n => n.type === 2);

	const getFeaturePriceById = (featureId: CheckboxValueType): number => {
		const feature = additionalFeatures.find(n => n.id === featureId);
		if (!feature || !feature.price) {
			return 0;
		}

		return feature.price;
	};

	const { t } = useTranslation();

	const onEmailChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		updateOrder({
			...order,
			contactEmail: e.target.value,
		});
	};

	const onChange = (list: CheckboxValueType[]): void => {
		setCheckedList(list);
		updateOrder({
			...order,
			relatedEntityIds: units.filter(unit => list.includes(unit.id)),
		});
		setCheckAll(list.length === plainOptions.length);
	};

	const onFeatureChange = (list: CheckboxValueType[]): void => {
		setCheckedFeaturesList(list);
		updateOrder({
			...order,
			additionalFeatures: list.map(String),
			additionalPriceForUnit: list.map(n => getFeaturePriceById(n)).reduce((a, b) => a + b, 0),
		});
	};

	const onCheckAllChange = (e: CheckboxChangeEvent): void => {
		setCheckedList(e.target.checked ? plainOptions.map(n => n.value) : []);
		updateOrder({
			...order,
			relatedEntityIds: e.target.checked ? units : [],
		});
		setCheckAll(e.target.checked);
	};

	const onChangeUnitsAutomaticallyChange = (e: CheckboxChangeEvent): void => {
		updateOrder({
			...order,
			addNewUnitOnOpening: e.target.checked,
		});
	};

	const onTermsOfServiceChange = (e: CheckboxChangeEvent): void => {
		toggleRulesAccepted(e.target.checked);
	};

	const clearAll = (): void => {
		setCheckedList([]);
		setCheckAll(false);
		updateOrder({
			...order,
			relatedEntityIds: [],
		});
	};

	if (!tariff.price) {
		return <div>error</div>;
	}

	return (
		<ChooseUnitsContainerStyled>
			{!!tariff.additionalOptions.length && (
				<>
					<ChooseUnitsSelectionHeadingStyled>Дополнительные функции:</ChooseUnitsSelectionHeadingStyled>
					<CheckboxGroup
						options={additionalFeaturesOptions}
						onChange={onFeatureChange}
						value={checkedFeaturesList}
						style={{ flexDirection: `column`, display: `flex`, gap: `7px`, marginBottom: `36px` }}
					/>
					<Divider style={{ margin: `12px 0` }} />
				</>
			)}
			<>
				<ChooseUnitsSelectionHeadingStyled>
					{t(`Marketplace_Select_Establishments`)}
				</ChooseUnitsSelectionHeadingStyled>
				{hasSpecialPrice && (
					<OldUsersContainerStyled>
						<ChooseUnitsTextStyled style={{ marginBottom: `0` }}>
							Вы переоформляете подписку на приложение через маркетплейс. Персональная цена для вашей сети
							сохраняется
						</ChooseUnitsTextStyled>
					</OldUsersContainerStyled>
				)}
				<ChooseUnitsSelectedContainerStyled>
					<Checkbox onChange={onCheckAllChange} checked={checkAll} style={{ marginBottom: `7px` }}>
						{t(`Marketplace_Select_All`)}
					</Checkbox>
					<Button type='link' onClick={clearAll} style={{ paddingRight: `0` }}>
						{t(`Marketplace_Clear_List`)}
					</Button>
				</ChooseUnitsSelectedContainerStyled>

				{plainOptions.length > 20 ? (
					<GridCheckboxGroupStyled options={plainOptions} onChange={onChange} value={checkedList} />
				) : (
					<CheckboxGroup
						options={plainOptions}
						onChange={onChange}
						value={checkedList}
						style={{
							flexDirection: `column`,
							display: `flex`,
							gap: `7px`,
							maxHeight: `200px`,
							flexWrap: `wrap`,
						}}
					/>
				)}

				{tariff.addNewUnitOnOpening && (
					<Checkbox
						onChange={onChangeUnitsAutomaticallyChange}
						checked={order.addNewUnitOnOpening}
						style={{ margin: `16px 0` }}
					>
						{t(`Marketplace_Automatic_Addition_New_Establishment`)}
					</Checkbox>
				)}
			</>
			<Divider style={{ margin: `0`, marginTop: `16px` }} />
			{order.relatedEntityIds.length !== 0 && precalculatedPrice.pricePerUnits !== 0 && (
				<OrderInfoContainerStyled>
					<OrderInfoItemStyled>
						<OrderInfoItemTextStyled>{t(`Marketplace_Selected`)}</OrderInfoItemTextStyled>
						<OrderInfoItemTextStyled>{`${checkedList.length} ${t(
							`Marketplace_Establishments`,
						)}`}</OrderInfoItemTextStyled>
					</OrderInfoItemStyled>
					{!hasFixedPrice && tariff.addNewUnitOnOpening && (
						<OrderInfoItemStyled>
							<OrderInfoItemTextStyled>
								{t(`Marketplace_Price_Per_Establishment`)}
								{!!tariff.tariffOverrides.length && (
									<Popover
										content={
											<PricePerUnit
												overrides={tariff.tariffOverrides.filter(n => n.type === 0)}
											/>
										}
										placement='bottom'
									>
										<QuestionCircleOutlined style={{ marginLeft: `4px` }} />
									</Popover>
								)}
							</OrderInfoItemTextStyled>
							<OrderInfoItemTextStyled>{`${precalculatedPrice.pricePerUnits} ₽ /${t(
								`Marketplace_Per_Month`,
							)}`}</OrderInfoItemTextStyled>
						</OrderInfoItemStyled>
					)}
					{hasFixedPrice && tariff.addNewUnitOnOpening && (
						<OrderInfoItemStyled>
							<OrderInfoItemTextStyled>{t(`Marketplace_Price`)}</OrderInfoItemTextStyled>
							<FixedPriceContainerStyled>
								<OrderInfoItemTextStyled>
									{`${tariff.tariffOverrides.filter(n => n.type === 2)[0].price.toLocaleString()} ₽`}
								</OrderInfoItemTextStyled>
								{hasFixedPrice.toNumberOfUnits &&
									order.relatedEntityIds.length > hasFixedPrice.toNumberOfUnits && (
										<AdditionalPriceStyled>{`+ ${
											tariff.price *
											(order.relatedEntityIds.length - hasFixedPrice.toNumberOfUnits)
										} ₽`}</AdditionalPriceStyled>
									)}
							</FixedPriceContainerStyled>
						</OrderInfoItemStyled>
					)}
				</OrderInfoContainerStyled>
			)}
			<Divider style={{ margin: `0` }} />
			<PriceSummaryContainerStyled>
				<PriceSummaryHeadingStyled>{t(`Marketplace_Total`)}</PriceSummaryHeadingStyled>
				<SubscribePrice
					price={order.relatedEntityIds.length === 0 ? 0 : precalculatedPrice.price}
					loading={isPrecalculationLoading}
					isTrialAvailable={tariff.isTrialAvailable}
					trial={trial}
					hasSpecialPrice={hasSpecialPrice}
				/>
			</PriceSummaryContainerStyled>
			{!tariff.isTrialAvailable && (
				<>
					<Divider style={{ margin: `16px 0` }} />
					<div style={{ marginTop: `0` }}>
						<ModalDropdown
							header={t(`Marketplace_Payment_Process`)}
							content={
								<ChooseUnitsTextStyled style={{ fontSize: `14px`, lineHeight: `22px` }}>
									{t(`Marketplace_No_Payment_Needed`)}
								</ChooseUnitsTextStyled>
							}
						/>
					</div>
				</>
			)}
			{trial && tariff.isTrialAvailable && (
				<>
					<Divider style={{ margin: 0, marginTop: `16px` }} />
					<BlockContainerStyled>
						<ModalDropdown
							header={t(`Marketplace_Days_For_Free`, { days: trial.duration })}
							content={
								<ChooseUnitsTextStyled style={{ fontSize: `14px`, lineHeight: `22px` }}>
									{t(`Marketplace_Trial_Message_Start`, { days: trial.duration })}
									<Link to='apps/subscriptions' rel='noreferrer' target='_blank'>
										{t(`Marketplace_Subscription_Management`)}
									</Link>
									{t(`Marketplace_Trial_Message_End`)}
								</ChooseUnitsTextStyled>
							}
						/>
					</BlockContainerStyled>
				</>
			)}
			<ChooseUnitsSelectionHeadingStyled style={{ marginBottom: `4px`, marginTop: `16px` }}>
				{t(`Marketplace_Leave_Email`)}
			</ChooseUnitsSelectionHeadingStyled>
			<ChooseUnitsTextStyled>
				{trial && tariff.isTrialAvailable
					? t(`Marketplace_Email_Message`)
					: t(`Marketplace_Email_Message_Without_Trial`)}
			</ChooseUnitsTextStyled>
			<Input
				type='contactEmail'
				placeholder={t(`Marketplace_Your_Email`) || ``}
				onChange={onEmailChange}
				status={order.contactEmail?.length && !isValidEmailAddress(order.contactEmail) ? `error` : ``}
				style={{ padding: `16px`, fontSize: `16px`, borderRadius: `8px`, marginTop: `12px` }}
			/>
			{!isValidEmailAddress(order.contactEmail) && !!order.contactEmail?.length && (
				<InputErrorMessage>{t(`Marketplace_Invalid_Email`)}</InputErrorMessage>
			)}
			<TermsOfServiceContainerStyled>
				<Checkbox onChange={onTermsOfServiceChange} style={{ alignSelf: `start` }} />
				<ChooseUnitsTextStyled>
					{t(`Marketplace_Acceptance`)}
					{` `}
					<a
						href='https://docs.google.com/document/d/1sFPYQN75p5GvYytO2bKMDYOBCipDUSXq/edit'
						target='_blank'
						rel='noreferrer'
					>
						{t(`Marketplace_User_Agreement`).toLocaleLowerCase()}
					</a>
					{` `}
					{t(`Marketplace_Obligation_To_Pay_Invoice`)}
				</ChooseUnitsTextStyled>
			</TermsOfServiceContainerStyled>
		</ChooseUnitsContainerStyled>
	);
};
